// Customizable dashboard — 20 widget options via WidgetPicker (localStorage-persisted).
import { useEffect, useMemo, useState, useCallback } from "react";
import { motion } from "framer-motion";
import { Link } from "react-router-dom";
import api from "@/lib/api";
import { useAuth } from "@/auth/AuthContext";
import { useI18n } from "@/i18n/I18nContext";
import { Plus, CalendarDays, Sliders, Lock, ArrowRight } from "lucide-react";

import { WIDGETS, loadEnabledWidgets, saveEnabledWidgets } from "@/components/dashboard/widgetConfig";
import { RENDERERS } from "@/components/dashboard/widgets";
import WidgetPicker from "@/components/dashboard/WidgetPicker";
import WidgetDetailModal, { DashboardCtx } from "@/components/dashboard/WidgetDetailModal";
import WhatsNewDrawer from "@/components/WhatsNewDrawer";
import OnboardingChecklist from "@/components/OnboardingChecklist";
import { Skeleton, SkeletonCard } from "@/components/Skeleton";

// Live clock that self-updates every 30 seconds — subtle "the app is alive" signal
// near the greeting header.
function useLiveClock() {
  const [now, setNow] = useState(() => new Date());
  useEffect(() => {
    const id = setInterval(() => setNow(new Date()), 30 * 1000);
    return () => clearInterval(id);
  }, []);
  return now;
}

function greetingKey(t) {
  const h = new Date().getHours();
  // NL time-of-day boundaries: nacht (0-5), morgen (6-11), middag (12-16), avond (17-21), nacht (22-23)
  if (h < 6) return t("greeting_night");
  if (h < 12) return t("greeting_morning");
  if (h < 17) return t("greeting_afternoon");
  if (h < 22) return t("greeting_evening");
  return t("greeting_night");
}

export default function Dashboard() {
  const { user } = useAuth();
  const { t, lang } = useI18n();
  const liveClock = useLiveClock();

  const [enabledIds, setEnabledIds] = useState(() => loadEnabledWidgets());
  const [pickerOpen, setPickerOpen] = useState(false);
  const [openWidgetId, setOpenWidgetId] = useState(null);
  const [loading, setLoading] = useState(true);
  // Feb 2026 — subscription paywall. Owners see a full-width banner when the
  // company has no active plan (new companies start `unpaid`). Non-owners
  // are not shown the banner (they'll get a toast if they try to mutate).
  const [billingLocked, setBillingLocked] = useState(false);
  const [data, setData] = useState({
    stats: null, today: null, notifications: [], weekShifts: [], myShifts: [],
    todayShifts: [], employees: [], allUsers: [], leaves: [], news: [],
    leaveBalance: null, payrollSummary: null, hoursContract: null,
  });

  const openDetail = useCallback((id) => setOpenWidgetId(id), []);
  const closeDetail = useCallback(() => setOpenWidgetId(null), []);
  const openWidget = useMemo(
    () => WIDGETS.find((w) => w.id === openWidgetId) || null,
    [openWidgetId]
  );

  const enabledSet = useMemo(() => new Set(enabledIds), [enabledIds]);
  const needsList = (id) => enabledSet.has(id);

  useEffect(() => {
    if (user?.role !== "owner") return;
    api.get("/billing/status")
      .then(({ data: s }) => setBillingLocked(!!s?.locked))
      .catch(() => {});
  }, [user?.role]);

  useEffect(() => {
    (async () => {
      try {
        const todayIso = new Date().toISOString().slice(0, 10);
        const week = new Date(Date.now() + 7 * 86400000).toISOString().slice(0, 10);
        // Base fetches always
        const [s, n, sh, td] = await Promise.all([
          api.get("/dashboard/stats").catch(() => ({ data: null })),
          api.get("/notifications").catch(() => ({ data: { items: [] } })),
          api.get(`/shifts?start=${todayIso}&end=${week}`).catch(() => ({ data: [] })),
          api.get("/today").catch(() => ({ data: null })),
        ]);
        // Optional fetches only if a related widget is enabled
        const optFetches = [];
        if (needsList("my_shifts")) {
          optFetches.push(
            api.get(`/shifts?start=${todayIso}&end=${week}&employee_id=${user?._id || ""}`)
              .then((r) => ["myShifts", r.data]).catch(() => ["myShifts", []])
          );
        }
        if (needsList("team_today") || needsList("pending_leave_list") || needsList("top_hours_employees")) {
          optFetches.push(
            api.get("/users?role=employee").then((r) => ["employees", r.data]).catch(() => ["employees", []])
          );
        }
        if (needsList("team_today")) {
          optFetches.push(
            api.get(`/shifts?start=${todayIso}&end=${todayIso}`).then((r) => ["todayShifts", r.data]).catch(() => ["todayShifts", []])
          );
        }
        if (needsList("pending_leave_list") || needsList("leave_taken_ytd")) {
          const url = needsList("leave_taken_ytd") ? "/leave-requests" : "/leave-requests?status=pending";
          optFetches.push(
            api.get(url).then((r) => ["leaves", r.data]).catch(() => ["leaves", []])
          );
        }
        if (needsList("recent_news")) {
          optFetches.push(
            api.get("/news").then((r) => ["news", r.data]).catch(() => ["news", []])
          );
        }
        // Fase 1 additions — only fetch when widget is enabled
        if (needsList("my_leave_balance")) {
          optFetches.push(
            api.get("/my/leave-balance").then((r) => ["leaveBalance", r.data]).catch(() => ["leaveBalance", null])
          );
        }
        if (needsList("my_expected_pay")) {
          optFetches.push(
            api.get("/my/payroll-summary?period=month").then((r) => ["payrollSummary", r.data]).catch(() => ["payrollSummary", null])
          );
        }
        if (needsList("hours_vs_contract")) {
          optFetches.push(
            api.get("/dashboard/hours-vs-contract").then((r) => ["hoursContract", r.data]).catch(() => ["hoursContract", null])
          );
        }
        if (needsList("team_composition")) {
          // Full user list — no role filter — so the donut can count all 4 roles.
          optFetches.push(
            api.get("/users").then((r) => ["allUsers", r.data]).catch(() => ["allUsers", []])
          );
        }
        const optResults = await Promise.all(optFetches);
        const opt = Object.fromEntries(optResults);
        setData({
          stats: s.data, today: td.data,
          notifications: n.data.items || [],
          weekShifts: sh.data || [],
          myShifts: opt.myShifts || [],
          todayShifts: opt.todayShifts || [],
          employees: opt.employees || [],
          allUsers: opt.allUsers || [],
          leaves: opt.leaves || [],
          news: opt.news || [],
          leaveBalance: opt.leaveBalance || null,
          payrollSummary: opt.payrollSummary || null,
          hoursContract: opt.hoursContract || null,
        });
      } finally { setLoading(false); }
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [enabledIds, user?._id]);

  const handleSave = (ids) => {
    setEnabledIds(ids);
    saveEnabledWidgets(ids);
  };

  const orderedWidgets = useMemo(
    () => enabledIds.map((id) => WIDGETS.find((w) => w.id === id)).filter(Boolean),
    [enabledIds]
  );

  return (
    <DashboardCtx.Provider value={{ openDetail }}>
    <div data-testid="page-dashboard" className="max-w-7xl">
      {/* Paywall banner — Owner only, when tenant has no active plan */}
      {billingLocked && user?.role === "owner" && (
        <motion.div
          initial={{ opacity: 0, y: -6 }} animate={{ opacity: 1, y: 0 }}
          className="mb-6 card p-5 md:p-6 border-l-[3px] border-l-[hsl(var(--warning))] relative overflow-hidden"
          style={{ background: "linear-gradient(135deg, hsl(var(--surface)) 0%, hsl(38 92% 60% / 0.10) 100%)" }}
          data-testid="dashboard-paywall-banner"
        >
          <div className="flex items-start gap-4">
            <div className="w-11 h-11 rounded-xl bg-[hsl(var(--warning-soft))] text-[hsl(var(--warning))] grid place-items-center shrink-0">
              <Lock size={18} strokeWidth={2} />
            </div>
            <div className="flex-1 min-w-0">
              <div className="font-display text-[18px] font-bold text-[hsl(var(--text-strong))] tracking-tight">
                {t("paywall_banner_title")}
              </div>
              <div className="text-[13.5px] text-[hsl(var(--text-muted))] mt-1 leading-relaxed max-w-2xl">
                {t("paywall_banner_body")}
              </div>
              <Link to="/billing" className="btn btn-primary mt-4 !text-[13.5px] group" data-testid="paywall-choose-plan">
                {t("paywall_choose_plan")}
                <ArrowRight size={14} strokeWidth={2.2} className="transition-transform group-hover:translate-x-0.5" />
              </Link>
            </div>
          </div>
        </motion.div>
      )}

      {/* Hero */}
      <motion.div
        initial={{ opacity: 0, y: -6 }} animate={{ opacity: 1, y: 0 }}
        className="flex flex-col md:flex-row md:items-end md:justify-between gap-4 mb-8"
      >
        <div>
          <div className="eyebrow mb-3 inline-flex items-center gap-2">
            {new Date().toLocaleDateString("nl", { weekday: "long", day: "numeric", month: "long" })}
            <span className="w-1 h-1 rounded-full bg-[hsl(var(--text-muted))] opacity-50" />
            <span className="tabular-nums" data-testid="live-clock">
              {liveClock.toLocaleTimeString("nl", { hour: "2-digit", minute: "2-digit" })}
            </span>
          </div>
          <h1 className="font-display text-[36px] md:text-[44px] leading-[1.05] tracking-tight text-[hsl(var(--text-strong))] flex items-center gap-3">
          <span>{greetingKey(t)}, {user?.name?.split(" ")[0] || "team"}.</span>
          </h1>
          <p className="text-[14px] text-[hsl(var(--text-muted))] mt-2 max-w-md">{t("dashboard_subtitle")}</p>
        </div>
        <div className="flex items-center gap-2 flex-wrap">
          <button onClick={() => setPickerOpen(true)} data-testid="open-widget-picker" className="btn btn-ghost">
            <Sliders size={14} strokeWidth={2} /> {t("customize_dashboard")}
          </button>
          <Link to="/roster" className="btn btn-ghost" data-testid="quick-open-roster">
            <CalendarDays size={16} strokeWidth={1.75} />{t("roster")}
          </Link>
          <Link to="/employees" className="btn btn-primary" data-testid="quick-add-employee">
            <Plus size={16} strokeWidth={2} />{t("add_employee")}
          </Link>
        </div>
      </motion.div>

      {/* Onboarding checklist — Owner only, hides once all steps done or dismissed. */}
      {user?.role === "owner" && !billingLocked && (
        <OnboardingChecklist stats={data.stats} />
      )}

      {/* Widget grid — 4-col base, widgets span their size */}
      {loading ? (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4" data-testid="dashboard-skeleton">
          {Array.from({ length: 4 }).map((_, i) => <Skeleton key={`s${i}`} className="h-[148px]" rounded="rounded-3xl" />)}
          <SkeletonCard className="md:col-span-2" />
          <SkeletonCard className="md:col-span-2" />
        </div>
      ) : orderedWidgets.length === 0 ? (
        <div className="card p-16 text-center" data-testid="dashboard-empty">
          <div className="w-16 h-16 rounded-2xl bg-[hsl(var(--primary-soft))] text-[hsl(var(--primary))] grid place-items-center mx-auto mb-4">
            <Sliders size={26} strokeWidth={1.8} />
          </div>
          <h3 className="font-display text-[22px] font-bold tracking-tight mb-2">{t("dashboard_empty_title")}</h3>
          <p className="text-[13px] text-[hsl(var(--text-muted))] mb-6 max-w-sm mx-auto">{t("dashboard_empty_desc")}</p>
          <button onClick={() => setPickerOpen(true)} className="btn btn-primary">
            <Sliders size={14} strokeWidth={2} /> {t("customize_dashboard")}
          </button>
        </div>
      ) : (
        <div className="space-y-6">
          {(() => {
            // Group widgets by SIZE — small (1) up top in a 4-col grid,
            // medium (2) below in a 2-col grid, and full-width (4) at the bottom.
            const widgetMeta = (id) => WIDGETS.find((x) => x.id === id);
            const smalls = orderedWidgets.filter((w) => widgetMeta(w.id)?.size === 1);
            const mediums = orderedWidgets.filter((w) => widgetMeta(w.id)?.size === 2);
            const larges = orderedWidgets.filter((w) => (widgetMeta(w.id)?.size || 2) >= 4);

            const Row = ({ children }) => children;
            const sections = [];

            if (smalls.length > 0) {
              sections.push(
                <div key="row-small">
                  <div className="section-header" data-testid="section-kpis">
                    {t("dashboard_row_kpis") || "Kerncijfers"}
                  </div>
                  <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-3 md:gap-4">
                    {smalls.map((w) => {
                      const Comp = RENDERERS[w.id];
                      if (!Comp) return null;
                      return (
                        <div key={w.id} data-testid={`widget-slot-${w.id}`}>
                          <Comp {...data} t={t} lang={lang} />
                        </div>
                      );
                    })}
                  </div>
                </div>
              );
            }
            if (mediums.length > 0) {
              sections.push(
                <div key="row-medium">
                  <div className="section-header" data-testid="section-details">
                    {t("dashboard_row_details") || "Details & inzichten"}
                  </div>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    {mediums.map((w) => {
                      const Comp = RENDERERS[w.id];
                      if (!Comp) return null;
                      return (
                        <div key={w.id} data-testid={`widget-slot-${w.id}`}>
                          <Comp {...data} t={t} lang={lang} />
                        </div>
                      );
                    })}
                  </div>
                </div>
              );
            }
            if (larges.length > 0) {
              sections.push(
                <div key="row-large" className="grid grid-cols-1 gap-4">
                  {larges.map((w) => {
                    const Comp = RENDERERS[w.id];
                    if (!Comp) return null;
                    return (
                      <div key={w.id} data-testid={`widget-slot-${w.id}`}>
                        <Comp {...data} t={t} lang={lang} />
                      </div>
                    );
                  })}
                </div>
              );
            }
            return <Row>{sections}</Row>;
          })()}
        </div>
      )}

      <WidgetPicker
        open={pickerOpen}
        enabled={enabledIds}
        onClose={() => setPickerOpen(false)}
        onSave={handleSave}
      />

      <WidgetDetailModal
        widget={openWidget}
        data={data}
        onClose={closeDetail}
        t={t}
      />
      <WhatsNewDrawer />
    </div>
    </DashboardCtx.Provider>
  );
}
